import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Manages the dynamic options list inside the custom-field edit modal :
//   - add an option (cloned from a <template>)
//   - remove an option (disabled when the option was already used in prod)
//   - drag-drop reorder via sortablejs
//   - auto-slugify the option internal_name from the visible name
// The Stimulus `form` outer controller listens for input events on rows
// to refresh the live preview.
export default class extends Controller {
  static targets = ["list", "template", "row", "optionName", "optionInternalName"];
  static values  = { locked: { type: Boolean, default: false } };

  connect() {
    this._sortable = Sortable.create(this.listTarget, {
      animation: 150,
      handle: ".cf-option__handle",
      ghostClass: "cf-option--ghost",
      onEnd: () => this._reindex(),
    });
    this._reindex();
  }

  disconnect() {
    if (this._sortable) this._sortable.destroy();
  }

  add(event) {
    if (event) event.preventDefault();
    const html = this.templateTarget.innerHTML.replace(/__INDEX__/g, String(this._nextIndex()));
    const wrapper = document.createElement("template");
    wrapper.innerHTML = html.trim();
    const node = wrapper.content.firstChild;
    this.listTarget.appendChild(node);
    this._reindex();
    const nameInput = node.querySelector('[data-custom-field-options-target="optionName"]');
    if (nameInput) nameInput.focus();
    this._notifyPreview();
  }

  remove(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.disabled) return;
    const row = button.closest('[data-custom-field-options-target="row"]');
    if (row) row.remove();
    this._reindex();
    this._notifyPreview();
  }

  onNameInput(event) {
    const row = event.target.closest('[data-custom-field-options-target="row"]');
    if (!row) return;
    const inameInput = row.querySelector('[data-custom-field-options-target="optionInternalName"]');
    if (!inameInput) return;
    if (inameInput.dataset.userEdited === "true") return;
    if (inameInput.dataset.locked === "true") return;
    inameInput.value = this._slugify(event.target.value);
  }

  onInternalNameInput(event) {
    event.target.dataset.userEdited = "true";
    event.target.value = (event.target.value || "").toLowerCase().replace(/[^a-z0-9_]/g, "");
  }

  // ----- helpers -----

  _reindex() {
    const rows = this.listTarget.querySelectorAll('[data-custom-field-options-target="row"]');
    rows.forEach((row, idx) => {
      row.querySelectorAll("input").forEach((input) => {
        const name = input.getAttribute("name");
        if (!name) return;
        input.setAttribute("name", name.replace(/\[options\]\[[^\]]+\]/, `[options][${idx}]`));
      });
    });
  }

  _nextIndex() {
    return this.listTarget.querySelectorAll('[data-custom-field-options-target="row"]').length;
  }

  _slugify(text) {
    return (text || "")
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^([0-9])/, "v_$1")
      .slice(0, 60);
  }

  _notifyPreview() {
    this.element.dispatchEvent(new CustomEvent("input", { bubbles: true }));
  }
}
